import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiCpu,
  FiCode,
  FiSmartphone,
  FiWifi,
  FiMessageSquare,
  FiZap,
  FiDatabase,
  FiBook,
  FiActivity,
  FiArrowRight,
} from 'react-icons/fi';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const PremiumServices = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);

  const services = [
    {
      icon: FiActivity,
      title: 'AI Development',
      description: 'Custom AI solutions and intelligent systems integrated into your product.',
      details: ['Deep Learning', 'Neural Networks', 'Custom AI Integration', 'Model Optimization'],
      color: 'from-primary-500 to-accent-500',
    },
    {
      icon: FiCpu,
      title: 'Machine Learning',
      description: 'Predictive modeling, classification, and statistical analytics.',
      details: ['Supervised Learning', 'Unsupervised Learning', 'Model Fine-tuning', 'Feature Engineering'],
      color: 'from-accent-500 to-primary-500',
    },
    {
      icon: FiMessageSquare,
      title: 'NLP Systems',
      description: 'Natural language processing, text processing, and style analytics.',
      details: ['Text Classification', 'Sentiment Analysis', 'Custom Transformers', 'Language Modeling'],
      color: 'from-primary-500 via-accent-500 to-primary-500',
    },
    {
      icon: FiCpu,
      title: 'Computer Vision',
      description: 'Image and video analytics, real-time object detection and segmentation.',
      details: ['Object Detection', 'Image Classification', 'YOLO Architectures', 'OpenCV Processing'],
      color: 'from-accent-500 to-primary-500',
    },
    {
      icon: FiZap,
      title: 'AI Chatbots',
      description: 'Conversational agents powered by LLMs and custom RAG pipelines.',
      details: ['RAG Pipeline Setup', 'LLM Fine-tuning', 'Dialog Management', 'API Integration'],
      color: 'from-primary-500 to-accent-500',
    },
    {
      icon: FiCode,
      title: 'Full Stack Development',
      description: 'End-to-end web applications with modern frontend and backend architectures.',
      details: ['React.js Frontend', 'Node.js/Express Backend', 'State Management', 'REST/GraphQL APIs'],
      color: 'from-accent-500 to-primary-500',
    },
    {
      icon: FiSmartphone,
      title: 'Flutter Development',
      description: 'Cross-platform mobile applications for high-performance iOS and Android apps.',
      details: ['Cross-Platform Apps', 'Firebase Integration', 'State Management', 'Native Device Features'],
      color: 'from-primary-500 to-accent-500',
    },
    {
      icon: FiWifi,
      title: 'Backend APIs',
      description: 'Secure, robust, and high-performance server architectures.',
      details: ['Microservices', 'RESTful API Design', 'JWT Authentication', 'Rate Limiting'],
      color: 'from-accent-500 to-primary-500',
    },
    {
      icon: FiDatabase,
      title: 'Database Systems',
      description: 'Database design, querying, caching, and schema optimization.',
      details: ['SQL Databases', 'NoSQL (MongoDB)', 'Query Optimization', 'Data Caching (Redis)'],
      color: 'from-primary-500 via-accent-500 to-primary-500',
    },
    {
      icon: FiBook,
      title: 'Technical Consulting',
      description: 'Strategic technology advice and architectural decision support.',
      details: ['Tech Stack Selection', 'Architecture Design', 'Feasibility Studies', 'Code Reviews'],
      color: 'from-accent-500 to-primary-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="services" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background Elements */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute -top-40 -right-40 w-80 h-80 bg-accent-500 rounded-full blur-3xl opacity-10"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="Services &" accent="Solutions" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Comprehensive services spanning AI, Full Stack Development, and Mobile Solutions
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, idx) => {
            const Icon = service.icon;
            return (
              <motion.a
                key={idx}
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('contact');
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                variants={itemVariants}
                onMouseEnter={() => setHoveredService(idx)}
                onMouseLeave={() => setHoveredService(null)}
                className="group cursor-pointer h-full block"
                whileHover={{ y: -12 }}
              >
                <div
                  className="relative h-full rounded-2xl overflow-hidden p-6 transition-all duration-300"
                  style={{
                    perspective: '1200px',
                    transformStyle: 'preserve-3d',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `rotateX(${y * 8}deg) rotateY(${x * 8}deg) translateZ(20px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0) rotateY(0) translateZ(0)';
                  }}
                >
                  {/* Background Gradient */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-5 group-hover:opacity-15 transition-opacity duration-300`}
                  />
                  <div className="absolute inset-0 bg-dark-800/50 backdrop-blur-sm group-hover:bg-dark-800/40 transition-all duration-300" />

                  {/* Content */}
                  <div className="relative flex flex-col h-full">
                    {/* Icon */}
                    <motion.div
                      animate={{
                        y: hoveredService === idx ? -5 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                      className={`mb-4 p-3 rounded-lg w-fit bg-gradient-to-br ${service.color} text-dark-900 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="text-2xl" />
                    </motion.div>

                    {/* Title & Description */}
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-primary-500 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-sm text-white/60 mb-4 flex-grow">
                      {service.description}
                    </p>

                    {/* Details */}
                    <div className="space-y-2">
                      {service.details.map((detail, detailIdx) => (
                        <motion.div
                          key={detailIdx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{
                            opacity: hoveredService === idx ? 1 : 0.6,
                            x: hoveredService === idx ? 0 : -10,
                          }}
                          transition={{ delay: detailIdx * 0.05 }}
                          className="flex items-center gap-2 text-xs text-white/70 group-hover:text-white/90 transition-colors"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                          {detail}
                        </motion.div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2 text-sm font-semibold text-primary-500 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                      Discuss this project
                      <FiArrowRight className="text-base group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>

                  {/* Border Glow */}
                  <div className="absolute inset-0 rounded-2xl border border-primary-500/0 group-hover:border-primary-500/40 transition-all duration-300" />
                </div>
              </motion.a>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumServices;
